import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, Search, CheckCircle2, Clock, Send } from "lucide-react";

const STATE = {
  delivered: { label: "Delivered", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  awaiting: { label: "Awaiting send", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  on_hold: { label: "On hold (unpaid)", cls: "bg-zinc-800 text-zinc-400 border-zinc-700" },
};
const FILTERS = [
  { key: "", label: "All" },
  { key: "on_hold", label: "On hold" },
  { key: "awaiting", label: "Awaiting send" },
  { key: "delivered", label: "Delivered" },
];

export default function AdminDeliveries() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  const load = () => {
    const params = {};
    if (status) params.status = status;
    if (q) params.q = q;
    api.get("/admin/deliveries", { params }).then(({ data }) => setData(data)).catch(() => setData({ rows: [], summary: {} }));
  };
  useEffect(load, [status, q]);

  const rows = data?.rows || [];
  const resendable = rows.filter((r) => r.paid);
  const toggle = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = resendable.length > 0 && resendable.every((r) => selected.has(r.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(resendable.map((r) => r.id)));

  const bulkResend = async (payload, label) => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/admin/deliveries/bulk-resend", payload);
      toast.success(`Sent ${res.sent} download email${res.sent === 1 ? "" : "s"}${res.failed ? ` · ${res.failed} failed` : ""}`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const resendSelected = () => {
    if (selected.size === 0) return toast.error("Select at least one recipient");
    if (!window.confirm(`Resend the download email to ${selected.size} recipient(s)?`)) return;
    bulkResend({ delivery_ids: [...selected] });
  };
  const resendAllAwaiting = () => {
    if (!window.confirm("Resend the download email to everyone currently awaiting send?")) return;
    bulkResend({ all_awaiting: true });
  };

  const s = data?.summary || {};
  const cards = [
    { label: "Total recipients", value: s.total || 0, icon: Send, cls: "text-white" },
    { label: "Paid", value: s.paid || 0, icon: CheckCircle2, cls: "text-emerald-400" },
    { label: "On hold", value: s.on_hold || 0, icon: Clock, cls: "text-amber-400" },
    { label: "Awaiting send", value: s.awaiting || 0, icon: Send, cls: "text-cyan-400" },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono-x text-xs uppercase tracking-widest text-zinc-500">Delivery</div>
          <h1 className="font-display text-2xl font-extrabold uppercase tracking-tight text-white sm:text-3xl">Delivery dashboard</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="resend-selected" onClick={resendSelected} disabled={busy || selected.size === 0} variant="outline" className="h-9 border-zinc-700 bg-transparent text-zinc-200 hover:bg-zinc-800 disabled:opacity-40">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-1.5 h-4 w-4" /> Resend selected{selected.size ? ` (${selected.size})` : ""}</>}
          </Button>
          <Button data-testid="resend-all-awaiting" onClick={resendAllAwaiting} disabled={busy || (s.awaiting || 0) === 0} className="h-9 bg-[#ff2a5f] font-semibold text-white hover:bg-[#e01f50] disabled:opacity-40">
            <Send className="mr-1.5 h-4 w-4" /> Resend all awaiting
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {cards.map((c) => { const Icon = c.icon; return (
          <div key={c.label} data-testid={`stat-${c.label}`} className="rounded-xl border border-zinc-800 bg-[#0d0d10] p-5">
            <Icon className={`mb-2 h-5 w-5 ${c.cls}`} />
            <div className={`font-display text-3xl font-extrabold ${c.cls}`}>{c.value}</div>
            <div className="text-xs text-zinc-500">{c.label}</div>
          </div>
        ); })}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-zinc-500" />
          <Input data-testid="delivery-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email or media…" className="h-9 border-zinc-700 bg-zinc-950 pl-9 text-sm text-white" />
        </div>
        {FILTERS.map((f) => (
          <button key={f.key} data-testid={`delivery-filter-${f.key || "all"}`} onClick={() => setStatus(f.key)}
            className={`rounded-full border px-4 py-1.5 text-sm font-medium ${status === f.key ? "border-[#ff2a5f] bg-[#ff2a5f] text-white" : "border-zinc-700 text-zinc-400 hover:text-white"}`}>{f.label}</button>
        ))}
      </div>

      <div className="overflow-x-auto rounded-xl border border-zinc-800 bg-[#0d0d10]">
        {data === null ? (
          <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-[#ff2a5f]" /></div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableHead className="w-10 text-zinc-400">
                  <input data-testid="select-all-deliveries" type="checkbox" checked={allSelected} onChange={toggleAll} disabled={resendable.length === 0} className="h-4 w-4 cursor-pointer accent-[#ff2a5f]" />
                </TableHead>
                <TableHead className="text-zinc-400">Email</TableHead>
                <TableHead className="text-zinc-400">Media</TableHead>
                <TableHead className="text-zinc-400">Paid</TableHead>
                <TableHead className="text-zinc-400">Email sent</TableHead>
                <TableHead className="text-zinc-400">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow className="border-zinc-800"><TableCell colSpan={6} className="py-12 text-center text-zinc-500">No deliveries match.</TableCell></TableRow>
              ) : rows.map((r) => { const st = STATE[r.state] || STATE.on_hold; return (
                <TableRow key={r.id} data-testid={`delivery-row-${r.id}`} className="border-zinc-800">
                  <TableCell>
                    {r.paid && <input data-testid={`select-delivery-${r.id}`} type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} className="h-4 w-4 cursor-pointer accent-[#ff2a5f]" />}
                  </TableCell>
                  <TableCell className="text-white">{r.email}</TableCell>
                  <TableCell className="text-zinc-300">{r.media_title}</TableCell>
                  <TableCell>{r.paid ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <Clock className="h-4 w-4 text-amber-400" />}</TableCell>
                  <TableCell className="text-zinc-400">{r.email_sent ? "Yes" : "No"}</TableCell>
                  <TableCell><span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${st.cls}`}>{st.label}</span></TableCell>
                </TableRow>
              ); })}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
